import {MouseEventHandler, ReactNode} from 'react';
import {Link as RouterLink} from 'react-router-dom';

import cn from 'classnames';

import {ButtonLinkMods, ButtonLinkSize} from './ButtonLink';

import s from './ButtonLink.module.css';

export interface LinkProps {
  id?: string;
  href: string;
  mod?: ButtonLinkMods;
  title?: string;
  onClick?: MouseEventHandler<HTMLAnchorElement>;
  dataQa?: string;
  className?: string;
  classNameContentContainer?: string;
  children?: ReactNode;
  isDisabled?: undefined;
  size?: ButtonLinkSize;
  type?: undefined;
}

export function Link({
  id,
  mod = ButtonLinkMods.ButtonYellow,
  title,
  className,
  classNameContentContainer,
  children,
  dataQa,
  href,
  size = undefined,
  onClick,
}: LinkProps): JSX.Element {
  const props = {
    id,
    ['data-qa']: dataQa,
    className: cn(s[`${mod}`], className, {
      [s.sizeXS]: size === ButtonLinkSize.XS,
      [s.sizeS]: size === ButtonLinkSize.S,
      [s.sizeM]: size === ButtonLinkSize.M,
      [s.sizeL]: size === ButtonLinkSize.L,
    }),
    title: title,
    ['aria-label']: title,
  };
  const isEnableContentContainer =
    mod === ButtonLinkMods.ButtonPrimary ||
    mod === ButtonLinkMods.ButtonSecondary ||
    mod === ButtonLinkMods.ButtonHexadecimal;

  const content = isEnableContentContainer ? (
    <span className={cn(s.text, classNameContentContainer)}>{children}</span>
  ) : (
    children
  );

  const isExternalLink = href.startsWith('http') || href.startsWith('mailto');
  if (isExternalLink) {
    return (
      <a {...props} onClick={onClick} href={href} target="_blank" rel="noopener noreferrer">
        {content}
      </a>
    );
  }

  const isInternalLink = href.startsWith('/');
  if (isInternalLink) {
    return (
      <RouterLink {...props} to={href} onClick={onClick}>
        {content}
      </RouterLink>
    );
  }

  // переход по deeplink при отображении проекта в WebView МБ,
  // потому что внешние ссылки типа http открываются в том же окне WebView
  // и вернуться назад можно только закрыв страницу
  const isDeepLink = href.startsWith('tcsgames');
  if (isDeepLink) {
    return (
      <a {...props} onClick={onClick} href={href} rel="noopener noreferrer">
        {content}
      </a>
    );
  }

  // Сейчас эта функция умеет доскроливать только на главной странице,
  // необходимо выполнить доработку, чтобы была возможность
  // перейти на другую страницу с доскролом
  // @See https://jira.tcsbank.ru/browse/SP-11639
  const scrollToElement: MouseEventHandler<HTMLAnchorElement> = (event) => {
    event.preventDefault();
    const scrollTo = document.querySelector(href);
    if (scrollTo) {
      scrollTo.scrollIntoView({block: 'start', behavior: 'smooth'});
    }

    if (typeof onClick === 'function') {
      onClick(event);
    }
  };

  return (
    <a {...props} onClick={scrollToElement}>
      {content}
    </a>
  );
}
